import React, { useMemo, useState } from "react";
import { Loader2, Megaphone, Power } from "lucide-react";
import Header from "components/Header";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  useGetAdsSettingsQuery,
  useUpdateAdsSettingsMutation,
  useUpdateAdScreenMutation,
} from "state/api";

const AdsSettings = () => {
  const { data, isLoading, isError, isFetching } = useGetAdsSettingsQuery();
  const [updateAdsSettings, { isLoading: isBulkSaving }] =
    useUpdateAdsSettingsMutation();
  const [updateAdScreen, { isLoading: isScreenSaving }] =
    useUpdateAdScreenMutation();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [pendingKey, setPendingKey] = useState(null);

  const masterEnabled = data?.adsMasterEnabled ?? true;
  const screens = data?.screens ?? [];
  const enabledCount = useMemo(
    () => screens.filter((s) => s.enabled).length,
    [screens]
  );
  const isSaving = isBulkSaving || isScreenSaving;

  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  const handleMasterToggle = async () => {
    showMessage("", "");
    try {
      await updateAdsSettings({ adsMasterEnabled: !masterEnabled }).unwrap();
      showMessage(
        "success",
        !masterEnabled
          ? "Ads master switch is ON. Per-screen settings apply."
          : "Ads master switch is OFF. No ads on any screen."
      );
    } catch (error) {
      showMessage("error", error?.data?.message || "Failed to update master switch.");
    }
  };

  const handleScreenToggle = async (screenKey, currentlyEnabled) => {
    if (!masterEnabled) {
      showMessage("error", "Turn on the master switch first to change per-screen ads.");
      return;
    }
    showMessage("", "");
    setPendingKey(screenKey);
    try {
      await updateAdScreen({
        screenKey,
        enabled: !currentlyEnabled,
      }).unwrap();
      showMessage(
        "success",
        `Ads ${!currentlyEnabled ? "enabled" : "disabled"} for this screen.`
      );
    } catch (error) {
      showMessage("error", error?.data?.message || "Failed to update screen.");
    } finally {
      setPendingKey(null);
    }
  };

  const handleSetAllScreens = async (enabled) => {
    if (!masterEnabled) {
      showMessage("error", "Turn on the master switch first.");
      return;
    }
    showMessage("", "");
    const screensPayload = Object.fromEntries(
      screens.map((s) => [s.key, enabled])
    );
    try {
      await updateAdsSettings({ screens: screensPayload }).unwrap();
      showMessage(
        "success",
        enabled ? "Ads enabled on all screens." : "Ads disabled on all screens."
      );
    } catch (error) {
      showMessage("error", error?.data?.message || "Failed to update screens.");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <Header
        title="Ads settings"
        subtitle="Turn ads on or off for each app screen"
      />

      {message.text && (
        <p
          className={`mt-4 text-sm ${
            message.type === "success" ? "text-green-600" : "text-red-600"
          }`}
        >
          {message.text}
        </p>
      )}

      <Card className="mt-6 max-w-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Power className="h-5 w-5" />
            Master switch
          </CardTitle>
          <CardDescription>
            When off, ads are hidden on every screen regardless of per-screen
            settings. When on, each screen uses its own toggle below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading ads settings…
            </div>
          ) : isError ? (
            <p className="text-sm text-red-600">Failed to load ads settings.</p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-medium">
                  Ads are {masterEnabled ? "enabled" : "disabled"} globally
                </p>
                <p className="text-sm text-muted-foreground">
                  {masterEnabled
                    ? `${enabledCount} of ${screens.length} screens show ads`
                    : "All screens are forced off"}
                </p>
              </div>
              <Button
                onClick={handleMasterToggle}
                disabled={isSaving || isFetching}
                variant={masterEnabled ? "default" : "outline"}
              >
                {isBulkSaving && !pendingKey ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {masterEnabled ? "Turn all ads OFF" : "Turn all ads ON"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-4 max-w-3xl">
        <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5" />
              Per-screen ads
            </CardTitle>
            <CardDescription className="mt-1.5">
              Toggle banners for each app screen. Changes save immediately.
            </CardDescription>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!masterEnabled || isSaving || isLoading}
              onClick={() => handleSetAllScreens(true)}
            >
              Enable all
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!masterEnabled || isSaving || isLoading}
              onClick={() => handleSetAllScreens(false)}
            >
              Disable all
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading screens…
            </div>
          ) : isError ? (
            <p className="text-sm text-red-600">Failed to load screens.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {screens.map((screen) => {
                const busy = pendingKey === screen.key;
                const checked = masterEnabled ? screen.enabled : false;
                return (
                  <li
                    key={screen.key}
                    className={`flex items-center justify-between gap-3 px-3 py-3 ${
                      !masterEnabled ? "opacity-60" : ""
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{screen.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {screen.description}
                      </p>
                      <p className="text-[10px] font-mono text-gray-400 mt-0.5">
                        {screen.key}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      ) : null}
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={checked}
                        disabled={!masterEnabled || isSaving}
                        onChange={() =>
                          handleScreenToggle(screen.key, screen.enabled)
                        }
                      />
                      <span className="text-xs font-semibold w-8">
                        {checked ? "ON" : "OFF"}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdsSettings;
